import { MarketingLayout } from './MarketingLayout'


function Section({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <section style={{ marginBottom: 48 }}>
      <h2 style={{
        fontFamily: 'var(--font-display)', fontSize: 20, fontWeight: 700,
        color: 'var(--navy-700)', marginBottom: 16,
        paddingBottom: 12, borderBottom: '1px solid var(--border-subtle)',
      }}>{title}</h2>
      {children}
    </section>
  )
}

function P({ children }: { children: React.ReactNode }) {
  return (
    <p style={{ fontSize: 14, color: 'var(--fg-2)', lineHeight: 1.85, marginBottom: 12 }}>
      {children}
    </p>
  )
}

function OL({ items }: { items: string[] }) {
  return (
    <ol style={{ paddingLeft: 20, margin: '0 0 12px' }}>
      {items.map((item, i) => (
        <li key={i} style={{ fontSize: 14, color: 'var(--fg-2)', lineHeight: 1.85, marginBottom: 6 }}>{item}</li>
      ))}
    </ol>
  )
}

export function TermsPage() {

  return (
    <MarketingLayout>
      <section style={{
        background: 'var(--navy-900, #0d1b2e)',
        padding: '72px var(--space-6) 56px',
      }}>
        <div style={{ maxWidth: 760, margin: '0 auto' }}>
          <h1 style={{
            fontFamily: 'var(--font-display)', fontSize: 'clamp(28px, 4vw, 42px)',
            fontWeight: 700, color: '#fff', letterSpacing: '-0.03em', marginBottom: 12,
          }}>Terms of Service</h1>
          <p style={{ fontSize: 14, color: 'var(--neutral-400)' }}>
            AutoAxxiz by JVS Ethics Lab · Last updated: 1 April 2026 · Effective immediately
          </p>
        </div>
      </section>

      <div style={{ padding: '72px var(--space-6)', background: '#fff' }}>
        <div style={{ maxWidth: 760, margin: '0 auto' }}>

          <div style={{
            background: 'rgba(217,110,26,0.06)', border: '1px solid rgba(217,110,26,0.2)',
            borderRadius: 10, padding: '16px 20px', marginBottom: 48,
          }}>
            <p style={{ fontSize: 13, color: 'var(--fg-2)', lineHeight: 1.7, margin: 0 }}>
              <strong>Plain-language summary:</strong> You subscribe monthly (or annually) in ZAR. We provide the software, you own your data, either party can terminate with reasonable notice. South African law governs. Full detail below.
            </p>
          </div>

          <Section title="1. Parties and agreement">
            <P>These Terms of Service ("Agreement") are between JVS Ethics Lab, the owner and operator of the AutoAxxiz product ("JVS Ethics Lab", "AutoAxxiz", "we", "us"), and the legal entity or individual that subscribes to the AutoAxxiz platform ("Customer", "you").</P>
            <P>By registering for an account, starting a trial, or using the platform, you agree to this Agreement in full. If you are registering on behalf of a company, you represent that you have authority to bind that company.</P>
          </Section>

          <Section title="2. The service">
            <P>AutoAxxiz is a cloud-hosted workshop management platform providing job card management, digital vehicle inspection, quoting, invoicing, WhatsApp Business API integration, accounting integrations, and related features (collectively, the "Service").</P>
            <P>We will make the Service available with a target uptime of 99.5% per calendar month, excluding scheduled maintenance and events outside our control (including Meta WhatsApp API outages, AWS infrastructure incidents, and South African load-shedding events above Stage 4 causing data-centre impact).</P>
            <P>We reserve the right to modify, update, or discontinue features with 30 days' notice, except for changes required by law, security patches, or Meta WhatsApp API compliance, which may take effect immediately.</P>
          </Section>

          <Section title="3. Subscription, pricing, and payment">
            <OL items={[
              'Subscription fees are quoted and billed by JVS Ethics Lab in South African rand (ZAR), exclusive of VAT.',
              'VAT at the current statutory rate (15%) is added where applicable. You receive a SARS-compliant tax invoice from JVS Ethics Lab monthly.',
              'Monthly subscriptions are billed on the same date each month. Annual subscriptions are billed upfront for 12 months.',
              'Payment is due within 7 days of invoice date. Late payment may result in service suspension after 14 days.',
              'WhatsApp Business API conversation costs are billed at Meta\'s published rates with zero markup, invoiced monthly on actual usage.',
              'We may increase subscription fees on 60 days\' written notice. You may cancel before the effective date without penalty.',
              'All refunds are at our discretion. The 30-day free trial period is excluded from refund claims.',
            ]} />
          </Section>

          <Section title="4. Free trial">
            <P>We offer a 30-day free trial with full platform access. No credit card is required to start a trial. At the end of the trial, your account is suspended until you subscribe. Your data is retained for 90 days after trial expiry, after which it is deleted unless you subscribe.</P>
          </Section>

          <Section title="5. Your data and POPIA compliance">
            <P>You own all data you input into the Service ("Customer Data"). We process Customer Data as an operator (as defined in POPIA) on your behalf and under your instructions.</P>
            <P>We store all Customer Data in Amazon Web Services af-south-1 (Cape Town, South Africa). Customer Data does not leave South Africa without your explicit consent, except where Meta processes WhatsApp message content on its own infrastructure outside South Africa under Meta's own privacy policy.</P>
            <P>Owners can export workshop records through Settings while their account is accessible. Contact support for export or deletion after account closure. Deletion timing, backup retention and applicable legal retention obligations must be confirmed for your deployment.</P>
            <P>For POPIA data processing enquiries, contact our Information Officer at: <strong>privacy@autoaxxiz.co.za</strong>.</P>
          </Section>

          <Section title="6. Acceptable use">
            <P>You agree not to use the Service to:</P>
            <OL items={[
              'Send WhatsApp messages that violate Meta\'s Messaging Policy or WhatsApp Business Policy.',
              'Store personal information beyond what is required for legitimate workshop administration purposes.',
              'Attempt to access, reverse-engineer, or circumvent any security controls of the platform.',
              'Resell or sublicense the Service to third parties.',
              'Process personal data of individuals under 18 without verified parental consent.',
              'Use the Service for any unlawful purpose under South African law.',
            ]} />
          </Section>

          <Section title="7. Invoicing and SARS compliance">
            <P>AutoAxxiz generates tax invoices that comply with the Value-Added Tax Act, 1991 (Act No. 89 of 1991) and SARS requirements. Invoice numbering follows the sequential INV-{'{year}'}-{'{sequence}'} format and numbers are never reused. You are responsible for maintaining your own VAT registration details accurately in the platform settings.</P>
            <P>AutoAxxiz does not constitute tax advice. Consult a registered tax practitioner for SARS submissions and compliance decisions.</P>
          </Section>

          <Section title="8. Intellectual property">
            <P>AutoAxxiz and all associated software, design, trademarks, and documentation are the intellectual property of JVS Ethics Lab. You receive a limited, non-exclusive, non-transferable licence to use the Service during the term of your subscription.</P>
            <P>You grant us a limited licence to store, process, and transmit your Customer Data solely to provide the Service.</P>
          </Section>

          <Section title="9. Limitation of liability">
            <P>To the maximum extent permitted by South African law, AutoAxxiz's total liability for any claim arising under this Agreement shall not exceed the total subscription fees paid by you in the 3 months preceding the claim.</P>
            <P>We are not liable for indirect, consequential, special, or punitive damages, including loss of revenue, loss of data, or business interruption. This limitation applies even if we have been advised of the possibility of such damages.</P>
            <P>Nothing in this clause limits liability for fraud, gross negligence, or wilful misconduct.</P>
          </Section>

          <Section title="10. Termination">
            <P>Either party may terminate this Agreement:</P>
            <OL items={[
              'By the Customer: at any time by cancelling the subscription in account settings before the next billing date.',
              'By AutoAxxiz: on 30 days\' written notice for any reason; immediately for material breach (including non-payment after 14 days, POPIA violation, or unlawful use).',
            ]} />
            <P>On termination, your access is suspended immediately. Customer Data is available for export for 30 days, then deleted.</P>
          </Section>

          <Section title="11. Governing law and disputes">
            <P>This Agreement is governed by the laws of the Republic of South Africa. Disputes shall be referred first to mediation. If unresolved after 30 days, disputes shall be subject to the jurisdiction of the South Gauteng High Court, Johannesburg.</P>
            <P>Nothing prevents either party from seeking urgent interim relief from a court of competent jurisdiction.</P>
          </Section>

          <Section title="12. Changes to these terms">
            <P>We may update these Terms with 30 days' notice via email or in-app notification. Your continued use of the Service after the effective date constitutes acceptance. If you do not accept updated terms, you may cancel before the effective date without penalty.</P>
          </Section>

          <Section title="13. Contact">
            <P>JVS Ethics Lab<br />
            AutoAxxiz product enquiries<br />
            Johannesburg, Gauteng, South Africa<br />
            Email: legal@autoaxxiz.co.za<br />
            POPIA enquiries: privacy@autoaxxiz.co.za</P>
          </Section>

        </div>
      </div>
    </MarketingLayout>
  )
}
